// ArtistChat.js - Optimized main chat component
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import ChatBanner from './ChatBanner';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { SettingsModal, ModerateModal, DeleteModal } from './ChatModals';

const ArtistChat = ({ artistName }) => {
  // State management - optimized with lazy initial state
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showModerateModal, setShowModerateModal] = useState(false);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [showBanner, setShowBanner] = useState(true);
  const [bannerMessage, setBannerMessage] = useState("Messages disappear after 24 hours");
  const [usernameColor, setUsernameColor] = useState(() => 
    localStorage.getItem('chatUsernameColor') || '#e5e7eb'
  );

  // Refs
  const socketRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const cleanupTimeoutRef = useRef(null);

  // Memoized values
  const currentUserId = useMemo(() => {
    return socketRef.current?.auth?.memberId || null;
  }, [socketRef.current?.auth?.memberId]);

  // Optimized message filtering with memoization
  const filteredMessages = useMemo(() => {
    const currentTime = Date.now();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    return messages
      .filter(msg => (currentTime - msg.timestamp) < twentyFourHours)
      .slice(-50); // Keep only last 50 messages for performance
  }, [messages]);

  // Optimized scroll handling with debounce
  const handleScroll = useCallback((e) => {
    const container = e.target;
    container.classList.add('scrolling');
    clearTimeout(container.scrollTimeout);
    container.scrollTimeout = setTimeout(() => {
      container.classList.remove('scrolling');
    }, 1000);
  }, []);

  // Optimized scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        container.scrollTop = container.scrollHeight;
      });
    }
  }, []);

  // Auto-scroll effect with optimization
  useEffect(() => {
    if (filteredMessages.length > 0) {
      scrollToBottom();
    }
  }, [filteredMessages, scrollToBottom]);

  // Optimized event handlers with useCallback
  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    
    if (!trimmedMessage || !isConnected) return;

    try {
      if (!socketRef.current) {
        throw new Error('Socket not initialized');
      }

      socketRef.current.emit('message', { 
        message: trimmedMessage,
        memberstackId: socketRef.current.auth.memberId,
        displayName: socketRef.current.auth.displayName,
        color: usernameColor
      }, (response) => {
        if (response?.error) {
          setError(`Failed to send message: ${response.error}`);
        } else {
          setMessage('');
        }
      });
    } catch (err) {
      setError(`Failed to send message: ${err.message}`);
    }
  }, [message, isConnected, usernameColor]);

  const handleDeleteMessage = useCallback(async (messageId) => {
    try {
      if (!socketRef.current) {
        throw new Error('Socket not initialized');
      }

      socketRef.current.emit('delete_message', { messageId }, (response) => {
        if (response?.error) {
          setError(`Failed to delete message: ${response.error}`);
        }
      });
    } catch (err) {
      setError(`Failed to delete message: ${err.message}`);
    }
  }, []);

  const handleBanUser = useCallback(async (memberstackId, displayName) => {
    try {
      if (!socketRef.current) {
        throw new Error('Socket not initialized');
      }

      socketRef.current.emit('command', { 
        command: 'ban',
        targetId: memberstackId,
        displayName: displayName
      }, (response) => {
        if (response?.error) {
          setError(`Failed to ban user: ${response.error}`);
        }
      });
    } catch (err) {
      setError(`Failed to ban user: ${err.message}`);
    }
  }, []);

  const handleUnbanUser = useCallback(async (memberstackId) => {
    // Optimistically update UI
    setBannedUsers(prev => prev.filter(user => user.memberstackId !== memberstackId));

    try {
      if (!socketRef.current) {
        throw new Error('Socket not initialized');
      }

      socketRef.current.emit('command', { 
        command: 'unban',
        targetId: memberstackId
      }, (response) => {
        if (response?.error) {
          setError(`Failed to unban user: ${response.error}`);
          // Revert optimistic update on error
          setBannedUsers(prev => [...prev, { memberstackId, displayName: 'Unknown User' }]);
        }
      });
    } catch (err) {
      setError(`Failed to unban user: ${err.message}`);
      // Revert optimistic update on error
      setBannedUsers(prev => [...prev, { memberstackId, displayName: 'Unknown User' }]);
    }
  }, []);

  const handleColorChange = useCallback((color) => {
    setUsernameColor(color);
    localStorage.setItem('chatUsernameColor', color);
  }, []);

  const handleModerationAction = useCallback((msg) => {
    setSelectedMessage(msg);
    setShowDeleteModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowDeleteModal(false);
    setSelectedMessage(null);
  }, []);

  // Optimized socket initialization
  const initializeSocket = useCallback(async () => {
    try {
      const cleanArtistName = artistName.replace('/s/', '').replace('s/', '');

      const member = await window.$memberstackDom.getCurrentMember();
      if (!member?.data) {
        throw new Error('Please log in to chat');
      }

      const token = localStorage.getItem('_ms-mid');
      if (!token) {
        throw new Error('No authentication token available');
      }

      const memberDataString = localStorage.getItem('_ms-mem');
      const memberData = memberDataString ? JSON.parse(memberDataString) : null;

      // Close existing socket if any
      if (socketRef.current) {
        socketRef.current.disconnect();
      }

      socketRef.current = io('https://chat.suprsketch.com', {
        auth: {
          token: token,
          memberId: memberData?.id,
          email: memberData?.auth?.email,
          displayName: memberData?.customFields?.name || memberData?.auth?.email?.split('@')[0],
          color: usernameColor
        },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 10000,
        forceNew: true,
        withCredentials: true
      });

      // Optimized event handlers
      socketRef.current.on('connect_error', (err) => {
        console.error('Socket connection error:', err);
        setIsConnected(false);
        setIsLoading(false);
        setError(err.message.includes('authentication') 
          ? 'Authentication failed. Please try logging in again.'
          : `Connection error: ${err.message}`);
      });

      socketRef.current.on('connect', () => {
        setIsConnected(true);
        setError(null);
        setIsLoading(false);

        socketRef.current.emit('join_room', { artistId: cleanArtistName }, (response) => {
          if (response?.error) {
            setError(`Failed to join chat: ${response.error}`);
          } else {
            // Get banned users after successful join
            socketRef.current.emit('get_banned_users', {}, (response) => {
              if (response?.bannedUsers) {
                setBannedUsers(response.bannedUsers);
              }
            });
          }
        });
      });

      // Optimized message handling
      socketRef.current.on('room_history', (history) => {
        const currentTime = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;
        const filteredHistory = history.filter(msg => (currentTime - msg.timestamp) < twentyFourHours);
        const sortedHistory = [...filteredHistory].sort((a, b) => a.timestamp - b.timestamp);
        setMessages(sortedHistory);
      });

      socketRef.current.on('message', (msg) => {
        setMessages(prev => {
          const updated = [...prev, msg];
          const currentTime = Date.now();
          const twentyFourHours = 24 * 60 * 60 * 1000;
          return updated.filter(m => (currentTime - m.timestamp) < twentyFourHours);
        });
      });

      socketRef.current.on('message_deleted', (data) => {
        setMessages(prev => prev.filter(msg => msg.id !== data.messageId));
      });

      socketRef.current.on('banned_users_update', (updatedUsers) => {
        setBannedUsers(updatedUsers);
      });

      socketRef.current.on('banner_update', (bannerData) => {
        setBannerMessage(bannerData.message);
      });

      socketRef.current.on('disconnect', (reason) => {
        setIsConnected(false);
        if (reason === 'io server disconnect') {
          socketRef.current.connect();
        }
      });

    } catch (err) {
      console.error('Chat initialization failed:', err);
      setError(err.message);
      setIsLoading(false);
      setIsConnected(false);
    }
  }, [artistName, usernameColor]);

  // Socket initialization effect
  useEffect(() => {
    if (artistName) {
      initializeSocket();
    }

    // Cleanup function
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, [artistName, initializeSocket]);

  // Periodic cleanup effect
  useEffect(() => {
    const cleanup = () => {
      setMessages(prev => {
        const currentTime = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;
        return prev.filter(msg => (currentTime - msg.timestamp) < twentyFourHours);
      });
    };

    cleanupTimeoutRef.current = setInterval(cleanup, 5 * 60 * 1000); // Every 5 minutes

    return () => {
      if (cleanupTimeoutRef.current) {
        clearInterval(cleanupTimeoutRef.current);
      }
    };
  }, []);

  // Moderation modal effect
  useEffect(() => {
    if (showModerateModal && socketRef.current) {
      socketRef.current.emit('get_banned_users', {}, (response) => {
        if (response?.error) {
          console.error('Error getting banned users:', response.error);
        } else if (response?.bannedUsers) {
          setBannedUsers(response.bannedUsers);
        }
      });
    }
  }, [showModerateModal]);

  // Loading state
  if (isLoading) {
    return (
      <div style={{
        width: '100%',
        maxWidth: '400px',
        margin: '20px auto',
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: 'transparent',
        borderRadius: '8px',
        border: '1px solid rgba(226, 232, 240, 0.2)'
      }}>
        <p style={{ color: '#e5e7eb' }}>Connecting to chat...</p>
      </div>
    );
  }

  // Login prompt
  if (error === 'Please log in to chat') {
    return (
      <div style={{
        width: '100%',
        maxWidth: '400px',
        margin: '20px auto',
        padding: '1rem',
        backgroundColor: 'transparent',
        border: '1px solid rgba(226, 232, 240, 0.2)',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <p style={{ color: '#ef4444', marginBottom: '1rem' }}>Please log in to chat</p>
        <button
          onClick={() => window.$memberstackDom?.openModal('login')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'rgba(59, 130, 246, 0.8)',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer'
          }}
        >
          Log In
        </button>
      </div>
    );
  }

  // Main chat render
  return (
    <div style={{
      width: '100%',
      maxWidth: '400px',
      height: '100%',
      margin: '20px auto',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      border: '1px solid rgba(55, 65, 81, 0.2)',
      borderRadius: '0',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Modals */}
      <DeleteModal
        showDeleteModal={showDeleteModal}
        onClose={closeModal}
        selectedMessage={selectedMessage}
        onDeleteMessage={handleDeleteMessage}
        onBanUser={handleBanUser}
      />
      <SettingsModal
        showSettings={showSettings}
        onClose={() => setShowSettings(false)}
        usernameColor={usernameColor}
        onColorChange={handleColorChange}
      />
      <ModerateModal
        showModerateModal={showModerateModal}
        onClose={() => setShowModerateModal(false)}
        bannedUsers={bannedUsers}
        onUnban={handleUnbanUser}
      />

      {/* Banner */}
      <ChatBanner
        message={bannerMessage}
        showBanner={showBanner}
        onClose={() => setShowBanner(false)}
      />

      {/* Messages Container */}
      <div 
        ref={messagesContainerRef}
        id="message-container"
        className="messages-container"
        onScroll={handleScroll}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column-reverse',
          height: '100%',
          padding: 'calc(1rem + 2px)',
          overflowY: 'auto',
          backgroundColor: 'transparent',
          color: '#ffffff',
          msOverflowStyle: 'none',
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
        }}
      >
        <style>{`
          .messages-container::-webkit-scrollbar {
            width: 6px;
            background-color: transparent;
          }
          .messages-container::-webkit-scrollbar-thumb {
            background-color: transparent;
            border-radius: 3px;
            transition: background-color 0.3s;
          }
          .messages-container::-webkit-scrollbar-track {
            background-color: transparent;
          }
          .messages-container.scrolling::-webkit-scrollbar-thumb {
            background-color: rgba(75, 85, 99, 0.3);
          }
          .artist-tooltip::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            margin-left: -6px;
            border-width: 6px;
            border-style: solid;
            border-color: rgba(17, 24, 39, 0.95) transparent transparent transparent;
          }
          .artist-emoji:hover + .artist-tooltip {
            opacity: 1 !important;
          }
          .message-wrapper:hover button {
            opacity: 1 !important;
          }
        `}</style>

        {/* Messages List */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {filteredMessages.map((msg, index) => (
            <ChatMessage
              key={msg.id || index}
              message={msg}
              currentUserId={currentUserId}
              userColor={usernameColor}
              artistName={artistName}
              onModerationAction={handleModerationAction}
            />
          ))}
        </div>
      </div>

      {/* Error Message or Input */}
      {error && error !== 'Please log in to chat' ? (
        <div style={{
          padding: '1rem',
          textAlign: 'center',
          color: '#ef4444',
          borderTop: '1px solid rgba(55, 65, 81, 0.2)',
          backgroundColor: 'transparent'
        }}>
          {error}
        </div>
      ) : (
        <ChatInput
          message={message}
          onMessageChange={setMessage}
          onSubmit={handleSendMessage}
          isConnected={isConnected}
          artistName={artistName}
          onSettingsClick={() => setShowSettings(true)}
          onModerateClick={() => setShowModerateModal(true)}
        />
      )}
    </div>
  );
};

export default ArtistChat;